export type AppScreenId =
  | 'birth_input'
  | 'natal_chart'
  | 'natal_interpretation'
  | 'natal_timing'
  | 'relationship'
  | 'relationship_result'
  | 'data_management'
  | 'settings';

export interface AppNavigationState {
  model: 'APP-NAVIGATION-STATE-0.1.0';
  activeScreen: AppScreenId;
  natalResultAvailable: boolean;
  relationshipResultAvailable: boolean;
}

export interface AppNavigationTransition {
  allowed: boolean;
  state: AppNavigationState;
  reason?: string;
}

const NATAL_RESULT_SCREENS: ReadonlySet<AppScreenId> = new Set([
  'natal_chart',
  'natal_interpretation',
  'natal_timing',
]);

function freezeState(state: Omit<AppNavigationState, 'model'>): AppNavigationState {
  return Object.freeze({ model: 'APP-NAVIGATION-STATE-0.1.0' as const, ...state });
}

export function createInitialAppNavigationState(): AppNavigationState {
  return freezeState({
    activeScreen: 'birth_input',
    natalResultAvailable: false,
    relationshipResultAvailable: false,
  });
}

export function navigateApp(
  state: AppNavigationState,
  target: AppScreenId,
): AppNavigationTransition {
  if (NATAL_RESULT_SCREENS.has(target) && !state.natalResultAvailable) {
    return Object.freeze({
      allowed: false,
      state,
      reason: '此畫面需要先完成一份有效的本命排盤結果。',
    });
  }
  if (target === 'relationship_result' && !state.relationshipResultAvailable) {
    return Object.freeze({
      allowed: false,
      state,
      reason: '此畫面需要先完成雙人關係分析。',
    });
  }
  return Object.freeze({
    allowed: true,
    state: freezeState({
      activeScreen: target,
      natalResultAvailable: state.natalResultAvailable,
      relationshipResultAvailable: state.relationshipResultAvailable,
    }),
  });
}

export function setNatalResultAvailability(
  state: AppNavigationState,
  available: boolean,
): AppNavigationState {
  const activeScreen = !available && NATAL_RESULT_SCREENS.has(state.activeScreen)
    ? 'birth_input'
    : state.activeScreen;
  return freezeState({
    activeScreen,
    natalResultAvailable: available,
    relationshipResultAvailable: state.relationshipResultAvailable,
  });
}

export function setRelationshipResultAvailability(
  state: AppNavigationState,
  available: boolean,
): AppNavigationState {
  const activeScreen = !available && state.activeScreen === 'relationship_result'
    ? 'relationship'
    : state.activeScreen;
  return freezeState({
    activeScreen,
    natalResultAvailable: state.natalResultAvailable,
    relationshipResultAvailable: available,
  });
}

export function appNavigationDisclosure(state: AppNavigationState): string {
  const status = [
    state.natalResultAvailable ? '本命結果已就緒' : '尚無本命結果',
    state.relationshipResultAvailable ? '雙人結果已就緒' : '尚無雙人結果',
  ].join('；');
  return `目前畫面：${state.activeScreen}；${status}。導航狀態只記錄畫面與結果是否存在，不保存出生資料、命盤內容或 fingerprint。`;
}
